// client/src/services/analyticsApi.js
// API service for social media analytics

/**
 * Fetch analytics overview (KPIs + per-platform breakdown)
 */
export async function fetchAnalyticsOverview(tenantId, options = {}) {
  const { days = 30 } = options;
  const params = new URLSearchParams({ days: String(days) });

  const res = await fetch(`/api/analytics/overview/${tenantId}?${params}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to fetch overview');
  return data;
}

/**
 * Fetch per-post analytics with media and platform details
 */
export async function fetchPostAnalytics(tenantId, options = {}) {
  const { days = 30, limit = 50, offset = 0, sort = 'date' } = options;
  const params = new URLSearchParams({
    days: String(days),
    limit: String(limit),
    offset: String(offset),
    sort,
  });

  const res = await fetch(`/api/analytics/posts/${tenantId}?${params}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to fetch post analytics');
  return data;
}

/**
 * Fetch trends (daily posting activity + metrics)
 */
export async function fetchAnalyticsTrends(tenantId, options = {}) {
  const { days = 30 } = options;
  const params = new URLSearchParams({ days: String(days) });

  const res = await fetch(`/api/analytics/trends/${tenantId}?${params}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to fetch trends');
  return data;
}

/**
 * Fetch follower growth data
 */
export async function fetchFollowerGrowth(tenantId, options = {}) {
  const { days = 90, platform } = options;
  const params = new URLSearchParams({ days: String(days) });
  if (platform) params.set('platform', platform);

  const res = await fetch(`/api/analytics/followers/${tenantId}?${params}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to fetch follower growth');
  return data;
}

/**
 * Daily history (server/services/analyticsHistory.js): per-day activity and
 * followers, clamped to the plan's analytics_history_days.
 * → { window: { days, limitDays, clamped, from }, series, followers, migrationPending? }
 */
export async function fetchAnalyticsHistory(tenantId, options = {}) {
  const { days = 365, platform } = options;
  const params = new URLSearchParams({ days: String(days) });
  if (platform) params.set('platform', platform);
  const res = await fetch(`/api/analytics/${tenantId}/history?${params}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to fetch history');
  return data;
}

/**
 * Fetch click tracking statistics
 */
export async function fetchClickStats(tenantId, options = {}) {
  const { days = 30, postId } = options;
  const params = new URLSearchParams({ days: String(days) });
  if (postId) params.set('postId', String(postId));

  const res = await fetch(`/api/tracking/stats/${tenantId}?${params}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to fetch click stats');
  return data;
}

/**
 * Format large numbers for display (e.g., 1234 -> "1.2K")
 */
export function formatMetricNumber(num) {
  if (num === null || num === undefined) return '0';
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toLocaleString();
}

/**
 * Platform display names and colors
 */
export const PLATFORM_META = {
  facebook:  { name: 'Facebook',  color: '#1877F2', icon: 'bi-facebook' },
  instagram: { name: 'Instagram', color: '#E4405F', icon: 'bi-instagram' },
  twitter:   { name: 'X/Twitter', color: '#1DA1F2', icon: 'bi-twitter-x' },
  linkedin:  { name: 'LinkedIn',  color: '#0A66C2', icon: 'bi-linkedin' },
  youtube:   { name: 'YouTube',   color: '#FF0000', icon: 'bi-youtube' },
  tiktok:    { name: 'TikTok',    color: '#69C9D0', icon: 'bi-tiktok' },
  pinterest: { name: 'Pinterest', color: '#BD081C', icon: 'bi-pinterest' },
  reddit:    { name: 'Reddit',    color: '#FF4500', icon: 'bi-reddit' },
  bluesky:   { name: 'Bluesky',   color: '#0085FF', icon: 'bi-cloud' },
  threads:   { name: 'Threads',   color: '#000000', icon: 'bi-threads' },
  twitch:    { name: 'Twitch',    color: '#9146FF', icon: 'bi-twitch' },

};
